import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, FileText } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { PolicyTypeBadge } from "@/components/PolicyTypeBadge";

const POLL_INTERVAL_MS = 30_000;
const TOAST_DURATION_MS = 8_000;
const MAX_BADGE_COUNT = 9;
const DESCRIPTION_PREVIEW_LEN = 60;

const TYPE_LABEL = {
  auto: "Auto", casa: "Casa", vita: "Vita", salute: "Salute",
  infortuni: "Infortuni", viaggio: "Viaggio", responsabilita_civile: "RC",
};

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ count: 0, items: [] });
  const [snapshot, setSnapshot] = useState(() => {
    try {
      const snap = JSON.parse(localStorage.getItem("notif_bell_snapshot") || "{}");
      return snap.count || 0;
    } catch { return 0; }
  });
  // Re-leggi snapshot quando cambia (custom event from Layout)
  useEffect(() => {
    const handler = () => {
      try {
        const snap = JSON.parse(localStorage.getItem("notif_bell_snapshot") || "{}");
        setSnapshot(snap.count || 0);
      } catch { setSnapshot(0); }
    };
    window.addEventListener("badges:reset", handler);
    return () => window.removeEventListener("badges:reset", handler);
  }, []);
  const displayCount = Math.max(0, data.count - snapshot);
  const seenIds = useRef(new Set());
  const firstLoad = useRef(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data: res } = await api.get("/notifications");
      setData(res);
      // Toast for newly arrived items (skip on first load)
      if (!firstLoad.current) {
        const fresh = (res.items || []).filter((i) => !seenIds.current.has(i.id));
        fresh.forEach((i) => {
          toast.info(`Nuova richiesta da ${i.client_name}`, {
            description: `${TYPE_LABEL[i.type] || i.type} · ${i.coverage_request.slice(0, DESCRIPTION_PREVIEW_LEN)}`,
            duration: TOAST_DURATION_MS,
            action: { label: "Apri", onClick: () => navigate("/quotes") },
          });
        });
      }
      seenIds.current = new Set((res.items || []).map((i) => i.id));
      firstLoad.current = false;
    } catch (e) { logger.error("Notifications poll failed:", e); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  const goToQuote = (id) => {
    setOpen(false);
    navigate("/quotes");
  };

  return (
    <div className="relative">
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative w-full flex items-center justify-center gap-2 rounded-md bg-white/5 hover:bg-white/10 text-sm py-2 transition-colors"
        aria-label="Notifiche"
      >
        <Bell className="h-4 w-4" />
        <span>Notifiche</span>
        {displayCount > 0 && (
          <span
            data-testid="notif-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center px-1"
          >
            {displayCount > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : displayCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute bottom-full left-0 right-0 mb-2 bg-white text-slate-900 rounded-lg shadow-xl border border-slate-200 overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Notifiche
            </span>
            <span className="text-[10px] text-slate-500">{data.count} nuove</span>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {/* Notifiche staff (es. offerte pagate, sospensione deadline, ecc.) */}
            {(data.staff_items || []).map((s) => {
              const isSuspDeadline = s.kind === "suspension_deadline";
              const title = s.title || (isSuspDeadline ? "⏰ Scadenza riattivazione" : (s.message || "Notifica"));
              const body = s.body || (isSuspDeadline ? s.message : null);
              return (
                <div
                  key={s.id}
                  data-testid={`notif-staff-${s.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors flex gap-3 ${isSuspDeadline ? "bg-orange-50/50 hover:bg-orange-100" : "hover:bg-green-50"}`}
                >
                  <button
                    className="min-w-0 flex-1 flex gap-3 text-left"
                    onClick={async () => {
                      try { await api.post(`/notifications/${s.id}/read`); } catch { /* ignore */ }
                      setOpen(false);
                      if (s.policy_id) navigate(`/policies?focus=${s.policy_id}`);
                      else if (s.link) navigate(s.link);
                      load();
                    }}
                  >
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${isSuspDeadline ? "bg-orange-100" : "bg-green-100"}`}>
                      <span className="text-base">{isSuspDeadline ? "⏰" : "💰"}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{title}</div>
                      {body && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{body}</p>}
                      <p className="text-[10px] text-slate-400 mt-1">{formatDate(s.created_at)}</p>
                    </div>
                  </button>
                  {s.whatsapp_url && (
                    <a
                      href={s.whatsapp_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`notif-wa-${s.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="self-center inline-flex items-center px-2 py-1 rounded bg-[#25D366] hover:bg-[#1EBE5A] text-white text-[11px] font-semibold whitespace-nowrap"
                      title="Apri WhatsApp cliente"
                    >
                      💬 WhatsApp
                    </a>
                  )}
                </div>
              );
            })}
            {data.items.length === 0 && (data.staff_items || []).length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-slate-400">
                Nessuna nuova notifica
              </div>
            ) : (
              data.items.map((i) => (
                <button
                  key={i.id}
                  data-testid={`notif-item-${i.id}`}
                  onClick={() => goToQuote(i.id)}
                  className="w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-blue-50 transition-colors flex gap-3"
                >
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{i.client_name}</span>
                      <PolicyTypeBadge code={i.type} size="xs" testid={`notif-type-badge-${i.id}`} />
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{i.coverage_request}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{formatDate(i.created_at)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
            <button
              data-testid="notif-view-all"
              onClick={() => { setOpen(false); navigate("/quotes"); }}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              Vedi tutti i preventivi →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
